// Shared input validation helpers.

// ReDoS-safe email check. The quantifiers are bounded (so the regex's
// backtracking is capped at a constant, not driven by input length), and the
// length guard (RFC 5321 limits an address to 254 chars) keeps worst-case work
// constant  - so a hostile input cannot cause super-linear runtime / DoS.
const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{1,63}$/;

/** True when `email` looks like a valid address. Safe against ReDoS. */
export function isValidEmail(email: string): boolean {
  const value = email.trim();
  return value.length > 0 && value.length <= 254 && EMAIL_RE.test(value);
}

// --- Phone ---------------------------------------------------------------
// We validate only the digit count of the national number (E.164 allows a
// subscriber number of up to 15 digits including the country code). We keep
// this permissive on format - the country code is chosen from a dropdown, so
// we only need to ensure the caller typed a plausible number.

/** Digits only, stripped of spaces, dashes, parentheses, dots and a leading +. */
export function digitsOnly(input: string): string {
  return input.replace(/\D+/g, '');
}

/**
 * True when `national` (the number typed after the dial code) has a plausible
 * length. Defaults: 6–14 digits, which comfortably covers every country while
 * rejecting empty / obviously-too-short input.
 */
export function isValidPhone(national: string, min = 6, max = 14): boolean {
  const d = digitsOnly(national);
  return d.length >= min && d.length <= max;
}

/**
 * Combine a dial code and a national number into an E.164-ish string
 * "+<code> <national-digits>". Returns '' when there is no number.
 */
export function toE164(dialCode: string, national: string): string {
  const d = digitsOnly(national);
  if (!d) return '';
  const code = dialCode.startsWith('+') ? dialCode : `+${digitsOnly(dialCode)}`;
  return `${code} ${d}`;
}
